import api from '@/lib/axios'

export interface BillingPlan {
  id: string
  name: string
  slug: string
  is_hidden: boolean
  price_cents: number
  billing_interval: string
  description: string | null
  max_projects: number | null
  max_documents_per_project: number | null
  max_fields_per_project: number | null
  max_processed_documents: number | null
  max_users: number | null
  max_storage_mb: number | null
  chat_enabled: boolean
  is_custom_pricing: boolean
  whitelabel_enabled: boolean
  credits_per_page_batch_mode: number
  chat_credits_per_message: number
  credit_discount_factor: number
  monthly_wallet_credits: number
  annual_price_cents: number | null
  annual_free_months: number
  annual_discount_percent: number
  stripe_annual_price_id: string | null
}

export interface OrgSubscription {
  id: string
  org_id: string
  plan_id: string
  plan_name: string | null
  plan_slug: string | null
  is_custom_pricing: boolean
  chat_enabled: boolean
  status: string
  current_period_start: string | null
  current_period_end: string | null
}

export interface Invoice {
  id: string
  org_id: string
  amount_cents: number
  status: string
  due_date: string | null
  paid_at: string | null
  pdf_url: string | null
  description: string | null
  created_at: string
}

export interface WalletBalance {
  // Credits-denominated since migration 015. 1 credit = 1 page at default model.
  balance_credits: number
}

export interface WalletTransaction {
  id: string
  type: string
  amount_credits: number
  balance_after_credits: number
  description: string | null
  created_at: string
}

export interface ChangePlanResponse {
  status: string
  message: string
  checkout_url?: string | null
  effective?: string | null
}

export interface BillingConfig {
  publishable_key: string | null
}

export const billingApi = {
  listPlans: () => api.get<BillingPlan[]>('/billing/plans').then((r) => r.data),

  getSubscription: () => api.get<OrgSubscription>('/billing/subscription').then((r) => r.data),

  createCheckout: (plan_id: string) =>
    api.post<{ checkout_url: string }>('/billing/checkout', { plan_id }).then((r) => r.data),

  changePlan: (plan_id: string, interval: string = 'month') =>
    api.post<ChangePlanResponse>('/billing/change-plan', { plan_id, interval }).then((r) => r.data),

  getBillingPortal: () => api.post<{ url: string }>('/billing/portal').then((r) => r.data),

  getBillingConfig: () => api.get<BillingConfig>('/billing/config').then((r) => r.data),

  listInvoices: () => api.get<Invoice[]>('/billing/invoices').then((r) => r.data),

  getWalletBalance: () => api.get<WalletBalance>('/wallet/balance').then((r) => r.data),

  listTransactions: () =>
    api.get<WalletTransaction[]>('/wallet/transactions').then((r) => r.data),

  // Topup: caller provides USD cents; server returns Stripe client_secret
  // plus the credits granted on success (computed at the published rate,
  // default $1 = 10 credits; bulk topups get bonuses).
  topupWallet: (amount_cents: number) =>
    api
      .post<{ client_secret: string; amount_cents: number; credits_granted: number }>(
        '/wallet/topup',
        { amount_cents }
      )
      .then((r) => r.data),
}
